import {
  MAX_RANGES_PER_DAY,
  validateHourRows,
  weekToRows,
} from './opening-hours';
import type { IDayHours, ITimeRange, TWeekHours } from './opening-hours';

/**
 * Pure edits of the opening-hours editor (plan §4.2.1). Every function
 * returns a new week (the editor replaces its state, never mutates it). A
 * range whose end is not later than its start ends the next day.
 */

/** The dynamic modal's name (`useModal().showDynamicDialog`). */
export const HOURS_EDITOR_MODAL = 'v2-store-hours';

/** What the editor resolves with: the week, or null for «No cargar horario». Cancelling resolves null instead of this. */
export interface IHoursEditorResult {
  week: TWeekHours | null;
}

/** The editor's rows, Monday first, as API day numbers (0 = Sunday). */
export const EDITOR_DAYS: readonly number[] = [1, 2, 3, 4, 5, 6, 0];
/** A day switched on without ranges starts here (the same range as `defaultWeek`). */
export const DEFAULT_RANGE: Readonly<ITimeRange> = {
  from: '12:00',
  to: '23:00',
};
/** «Agregar otro horario» proposes dinner when it fits; otherwise an empty range. */
export const EXTRA_RANGE: Readonly<ITimeRange> = { from: '19:00', to: '23:00' };

const DAYS_PER_WEEK = 7;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const TIME_INPUT = /^(\d{2}:\d{2})(:\d{2}(\.\d+)?)?$/;

function copyRange(range: ITimeRange): ITimeRange {
  return { from: range.from, to: range.to };
}

function copyDay(dayHours: IDayHours | undefined): IDayHours {
  return dayHours
    ? { open: dayHours.open, ranges: dayHours.ranges.map(copyRange) }
    : { open: false, ranges: [] };
}

/** A deep copy with exactly seven days (missing days read as closed). */
export function cloneWeek(week: TWeekHours): TWeekHours {
  return Array.from({ length: DAYS_PER_WEEK }, (_, day) => copyDay(week[day]));
}

function withDay(
  week: TWeekHours,
  day: number,
  dayHours: IDayHours
): TWeekHours {
  const next = cloneWeek(week);
  next[day] = dayHours;
  return next;
}

function isCompleteRange(range: ITimeRange): boolean {
  return TIME.test(range.from) && TIME.test(range.to);
}

/** `HH:MM:SS` (some browsers' time inputs) → `HH:MM`; anything else trimmed as is. */
export function normalizeTimeInput(value: string): string {
  const trimmed = value.trim();
  const match = TIME_INPUT.exec(trimmed);
  return match ? match[1] : trimmed;
}

/** The switch «Abre»: a day opened without ranges gets `DEFAULT_RANGE`; closing keeps its ranges until «Listo». */
export function setDayOpen(
  week: TWeekHours,
  day: number,
  open: boolean
): TWeekHours {
  const current = copyDay(week[day]);
  const ranges =
    open && current.ranges.length === 0
      ? [copyRange(DEFAULT_RANGE)]
      : current.ranges;
  return withDay(week, day, { open, ranges });
}

export function updateRange(
  week: TWeekHours,
  day: number,
  index: number,
  field: keyof ITimeRange,
  value: string
): TWeekHours {
  const current = copyDay(week[day]);
  if (index < 0 || index >= current.ranges.length) return cloneWeek(week);
  current.ranges[index] = {
    ...current.ranges[index],
    [field]: normalizeTimeInput(value),
  };
  return withDay(week, day, current);
}

/** Removing the last range of a day closes the day. */
export function removeRange(
  week: TWeekHours,
  day: number,
  index: number
): TWeekHours {
  const current = copyDay(week[day]);
  const ranges = current.ranges.filter((_, position) => position !== index);
  return withDay(
    week,
    day,
    ranges.length ? { open: current.open, ranges } : { open: false, ranges: [] }
  );
}

/** One more range (up to 3): `EXTRA_RANGE` when it adds no conflict, else an empty one to fill in. */
export function addRange(week: TWeekHours, day: number): TWeekHours {
  const current = copyDay(week[day]);
  if (current.ranges.length >= MAX_RANGES_PER_DAY) return cloneWeek(week);
  const proposed = withDay(week, day, {
    open: true,
    ranges: [...current.ranges, copyRange(EXTRA_RANGE)],
  });
  const before = invalidHoursDays(week);
  const fits = invalidHoursDays(proposed).every((invalid) =>
    before.includes(invalid)
  );
  if (fits) return proposed;
  return withDay(week, day, {
    open: true,
    ranges: [...current.ranges, { from: '', to: '' }],
  });
}

/**
 * The row that offers «Usar este horario todos los días» and whose ranges it
 * copies: the first open day of the week (Monday first) with a range, so a
 * store closed on Mondays still gets the shortcut (review round 4, m5).
 * null while no day is open.
 */
export function copySourceDay(week: TWeekHours): number | null {
  return (
    EDITOR_DAYS.find((day) => {
      const dayHours = week[day];
      return !!dayHours && dayHours.open && dayHours.ranges.length > 0;
    }) ?? null
  );
}

/** «Usar este horario todos los días»: the source day's ranges on every open day (closed days stay closed). */
export function copyDayToOpenDays(
  week: TWeekHours,
  sourceDay: number
): TWeekHours {
  const source = copyDay(week[sourceDay]);
  if (!source.open || source.ranges.length === 0) return cloneWeek(week);
  return cloneWeek(week).map((dayHours) =>
    dayHours.open
      ? { open: true, ranges: source.ranges.map(copyRange) }
      : dayHours
  );
}

/** «(termina al día siguiente)»: a complete range whose end is not later than its start. */
export function rangeEndsNextDay(range: ITimeRange): boolean {
  return isCompleteRange(range) && range.to <= range.from;
}

function dayHasOvernightRange(dayHours: IDayHours | undefined): boolean {
  return !!dayHours && dayHours.open && dayHours.ranges.some(rangeEndsNextDay);
}

/**
 * Days (0 = Sunday) that show «Revise los horarios»: an open day without
 * ranges, with more than 3 or with an empty/malformed time, and every day
 * whose stored rows break the API rules (an overlap, also one caused by the
 * previous day's overnight range, which is flagged too). Empty exactly when
 * `validateWeek` accepts the week.
 */
export function invalidHoursDays(week: TWeekHours): number[] {
  const days = cloneWeek(week);
  const invalid = new Set<number>();
  days.forEach((dayHours, day) => {
    if (!dayHours.open) return;
    if (
      dayHours.ranges.length === 0 ||
      dayHours.ranges.length > MAX_RANGES_PER_DAY ||
      !dayHours.ranges.every(isCompleteRange)
    )
      invalid.add(day);
  });
  // Incomplete days are already flagged: their rows would only add noise.
  const complete = days.map((dayHours, day) =>
    invalid.has(day) ? { open: false, ranges: [] } : dayHours
  );
  const rows = weekToRows(complete);
  for (let day = 0; day < DAYS_PER_WEEK; day++) {
    if (validateHourRows(rows.filter((row) => row.day === day))) continue;
    invalid.add(day);
    const previous = (day + DAYS_PER_WEEK - 1) % DAYS_PER_WEEK;
    if (dayHasOvernightRange(complete[previous])) invalid.add(previous);
  }
  return [...invalid].sort((a, b) => a - b);
}

/** What «Listo» hands back: closed days without ranges; a week with no open day means no hours (null). */
export function normalizeEditedWeek(week: TWeekHours): TWeekHours | null {
  const cleaned = cloneWeek(week).map((dayHours) =>
    dayHours.open ? dayHours : { open: false, ranges: [] }
  );
  return cleaned.some((dayHours) => dayHours.open) ? cleaned : null;
}

/**
 * Long weekday names, capitalised («Lunes»), indexed by API day number
 * (0 = Sunday). Formatted in UTC from fixed dates (2024-01-07 was a Sunday),
 * so the device time zone can never shift a day.
 */
export function longWeekdayNames(locale: string): string[] {
  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    timeZone: 'UTC',
  });
  return Array.from({ length: DAYS_PER_WEEK }, (_, day) => {
    const name = formatter.format(new Date(Date.UTC(2024, 0, 7 + day)));
    return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
  });
}
